import { applyDriverLocationEvent } from "@the-drop/live-driver-map";
import { WS_EVENT } from "@the-drop/types";
import { useEffect, useState } from "react";
import { useTransporterSocketInstance } from "../workspace/transporter-socket-context";

export type LiveDriverPositions = Record<string, { lat: number; lng: number }>;

/**
 * Listens for **`driver.location`** on the shared transporter socket and accumulates live positions
 * keyed by `driverId` (or `runId` fallback, via `applyDriverLocationEvent`). The authoritative drivers
 * list is the API-fetched fleet (`DashboardScreen.drivers`); this hook only provides position deltas
 * to overlay on that list.
 *
 * Returns `positions: Record<driverId, { lat, lng }>`. Consumers should look up positions by API-known
 * driverId rather than iterating the map — so drivers who have left their shift (and disappear from
 * the API list) are naturally not rendered. A `runId`-keyed entry from the package reducer would
 * similarly never match an API driverId and be silently skipped, which is fine.
 *
 * The `transporterId` argument is used only as a guard — the socket is already transporter-scoped via
 * the workspace shell that mounts `TransporterSocketProvider`. Keep it: a stale socket during an admin
 * scope switch shouldn't accumulate positions for a transporter the user is no longer viewing.
 *
 * Mirrors dispensary's `useDispensaryDashboardLive` pattern: one socket per app, multiple feature
 * hooks subscribe to the relevant events.
 */
export function useTransporterDriversLive(transporterId: string | null): LiveDriverPositions {
  const socket = useTransporterSocketInstance();
  const [positions, setPositions] = useState<LiveDriverPositions>({});

  useEffect(() => {
    if (!socket || !transporterId) return;

    const onDriverLocation = (raw: unknown) => {
      setPositions((prev) => {
        const result = applyDriverLocationEvent(prev, raw);
        return result.kind === "applied" ? result.next : prev;
      });
    };

    socket.on(WS_EVENT.DRIVER_LOCATION, onDriverLocation);
    return () => {
      socket.off(WS_EVENT.DRIVER_LOCATION, onDriverLocation);
    };
  }, [socket, transporterId]);

  return positions;
}
